import React from 'react';
import { cn } from '../../lib/utils';
import { PageTitle } from './PageTitle';

export interface PageLayoutProps {
  /** Icon element (will be wrapped with orange color) */
  icon: React.ReactNode;
  /** Page title */
  title: string;
  /** Subtitle below the title (omit or pass empty string to hide) */
  subtitle?: React.ReactNode;
  /** Optional node after the title (e.g. status badge) */
  titleSuffix?: React.ReactNode;
  /** Optional node at the end of the title row (e.g. back button), aligned right */
  headerRight?: React.ReactNode;
  /** Optional node below the subtitle (e.g. link to related entity) */
  subtitleExtra?: React.ReactNode;
  /** Page content */
  children: React.ReactNode;
  /** Optional additional class for the outer wrapper */
  className?: string;
  /** When true, children fill remaining height below the header (use with a parent that sets height, e.g. chat page) */
  fillContent?: boolean;
}

/**
 * Unified page layout: orange icon + title + subtitle header, then content.
 * The title block goes into the middle of AppHeader via PageTitle.
 * headerRight / subtitleExtra stay in the page, as a toolbar row above the content.
 * Use with AppLayout's max-w-9xl container for consistent page width.
 */
export const PageLayout: React.FC<PageLayoutProps> = ({
  icon,
  title,
  subtitle,
  titleSuffix,
  headerRight,
  subtitleExtra,
  children,
  className,
  fillContent = false,
}) => {
  const hasToolbar = headerRight != null || subtitleExtra != null;

  return (
    <div
      className={cn(
        fillContent ? 'flex flex-col h-full min-h-0 gap-3' : 'space-y-6',
        className
      )}
    >
      <PageTitle icon={icon} title={title} subtitle={subtitle} titleSuffix={titleSuffix} />

      {hasToolbar ? (
        <div className={cn('flex items-center gap-3 flex-wrap', fillContent && 'shrink-0')}>
          {subtitleExtra}
          {headerRight != null ? <div className="ml-auto">{headerRight}</div> : null}
        </div>
      ) : null}

      {fillContent ? (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">{children}</div>
      ) : (
        children
      )}
    </div>
  );
};

export default PageLayout;
